import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import ScheduleService from '@/services/ScheduleService'
import Loading from '@/widgets/Loading'
import BookingDetailsCard from './BookingDetailsCard'

function ScheduleDetailsPage() {
	const { id } = useParams()
	const navigate = useNavigate()

	const [booking, setBooking] = useState(null)
	const [loading, setLoading] = useState(true)
	const [error, setError] = useState('')
	const [deleting, setDeleting] = useState(false)

	useEffect(() => {
		let active = true
		const fetchBooking = async () => {
			setLoading(true)
			setError('')
			try {
				const { data, error: fetchError } = await ScheduleService.getById(id)
				if (!active) return
				if (fetchError) throw new Error(fetchError)
				if (!data) throw new Error('Booking not found')
				setBooking(data)
			} catch (err) {
				if (!active) return
				setError(err?.message || 'Unable to load booking details.')
			} finally {
				if (active) setLoading(false)
			}
		}

		if (id) {
			fetchBooking()
		} else {
			setError('Missing booking reference.')
			setLoading(false)
		}

		return () => {
			active = false
		}
	}, [id])

	const bookingId = useMemo(() => booking?._id ?? booking?.id ?? id ?? '', [booking, id])

	const handleEdit = useCallback(() => {
		if (!bookingId) return
		navigate(`/schedule/edit/${bookingId}`, { state: { anchorDate: booking?.start || null } })
	}, [bookingId, booking?.start, navigate])

	const handleDelete = useCallback(async () => {
		if (!bookingId || deleting) return
		const confirmed = window.confirm('Delete this booking? This action cannot be undone.')
		if (!confirmed) return

		setDeleting(true)
		const { success, error: deleteError } = await ScheduleService.deleteOne(bookingId)
		if (!success) {
			setError(deleteError || 'Unable to delete the booking. Please try again.')
			setDeleting(false)
			return
		}

		navigate('/schedule', { state: { message: 'Booking deleted successfully.' } })
	}, [bookingId, deleting, navigate])

	if (loading) {
		return <Loading />
	}

	if (!booking) {
		return (
			<div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
				<h1 className="text-lg font-semibold text-gray-900">Booking details</h1>
				<p className="mt-2 text-sm text-gray-700">{error || 'Booking not found.'}</p>
				<button
					type="button"
					onClick={() => navigate('/schedule')}
					className="mt-4 inline-flex items-center rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">
						Back to schedule
					</button>
			</div>
		)
	}

	return (
		<div className="space-y-6">
			{error && (
				<div className="rounded-md bg-yellow-50 p-4 text-sm text-yellow-800" role="alert">
					{error}
				</div>
			)}

			<BookingDetailsCard booking={booking} onEdit={handleEdit} onDelete={handleDelete} deleting={deleting} />
		</div>
	)
}

export default ScheduleDetailsPage
